/**
 * Document themes — the pure part (design-handoffs/2026-10-05-document-themes).
 *
 * Everything here is derived from the server's theme descriptors and the
 * document's `look`; nothing is stored. The editor theme (Light, Dark…) is a
 * different thing: it belongs to the person and never reaches the PDF.
 */
import type {
  CaptionPosition, DocumentLook, LookPaper, LookTables, TableDensity, TableStyle, ThemeDescriptor, ThemeId,
} from "@/lib/api";
import { darkPaperFor } from "./darkPaper";

// Tables (2f) -------------------------------------------------------------------
//
// Look → Tables is a paper setting. The style's default belongs to the theme
// (its `tablesDefault`), so a look keeps `style` only once the author picks
// one: switching theme then moves an unpicked paper to the new theme's
// default. Density and caption position have the same default everywhere.

export const TABLE_STYLES: readonly TableStyle[] = ["ruled", "banded", "header"];
export const TABLE_DENSITIES: readonly TableDensity[] = ["normal", "compact"];
export const CAPTION_POSITIONS: readonly CaptionPosition[] = ["above", "below"];

export function isTableStyle(v: unknown): v is TableStyle {
  return typeof v === "string" && (TABLE_STYLES as readonly string[]).includes(v);
}

/** Look → Tables as stored: a valid style or none, density and caption filled in. */
export function normaliseTables(tables: LookTables | null | undefined): LookTables {
  const t = tables && typeof tables === "object" ? tables : {};
  const out: LookTables = {
    density: t.density === "compact" ? "compact" : "normal",
    caption: t.caption === "below" ? "below" : "above",
  };
  if (isTableStyle(t.style)) out.style = t.style;
  return out;
}

/** Look → Tables with every part decided. */
export interface ResolvedTables {
  style: TableStyle;
  density: TableDensity;
  caption: CaptionPosition;
}

/**
 * What a paper's tables actually get: the author's style, else the theme's
 * default, else Ruled. Under a publisher class (`locked`) only Ruled.
 */
export function resolveTables(
  tables: LookTables | null | undefined,
  theme: Pick<ThemeDescriptor, "tablesDefault"> | null | undefined,
  locked = false,
): ResolvedTables {
  const t = normaliseTables(tables);
  const style: TableStyle = locked
    ? "ruled"
    : t.style ?? (isTableStyle(theme?.tablesDefault) ? theme!.tablesDefault as TableStyle : "ruled");
  return { style, density: t.density ?? "normal", caption: t.caption ?? "above" };
}

/** What a document without a look has. */
export const CLASSIC_LOOK: DocumentLook = {
  theme: "classic",
  paper: "theme",
  pins: {},
  tables: { density: "normal", caption: "above" },
};

/** The document's look, with `null` (and partial values) read as Classic. */
export function normaliseLook(look: DocumentLook | null | undefined): DocumentLook {
  if (!look || !look.theme) return CLASSIC_LOOK;
  return {
    theme: look.theme,
    paper: look.paper === "white" ? "white" : "theme",
    pins: look.pins && typeof look.pins === "object" ? look.pins : {},
    tables: normaliseTables(look.tables),
  };
}

/** A theme the author can pick today. */
export function isSelectable(theme: ThemeDescriptor): boolean {
  return theme.available && theme.status !== "planned";
}

/**
 * Why a theme can't be picked, in a word, or null when it can. A theme the
 * server marks unavailable (its fonts are not in the compile image yet) reads
 * exactly like a planned one: it never compiles with a fallback font.
 */
export function unavailableReason(theme: ThemeDescriptor): string | null {
  return isSelectable(theme) ? null : "Planned";
}

/**
 * Whether a document may use a theme. The server lists the themes the class
 * allows (`lookThemes`): a beamer deck takes Classic or Exposition, and
 * Exposition is for beamer only (Olivia, 6 Oct). No list means no limit.
 */
export function themeAllowed(id: ThemeId, lookThemes: readonly ThemeId[] | null | undefined): boolean {
  return !lookThemes || lookThemes.includes(id);
}

/** Why a theme is not for this document's class ("Beamer only"), or null. */
export function classReason(theme: ThemeDescriptor, lookThemes: readonly ThemeId[] | null | undefined): string | null {
  if (themeAllowed(theme.id, lookThemes)) return null;
  const classes = theme.classes?.filter(Boolean) ?? [];
  if (!classes.length) return "Not for this class";
  const names = classes.join(", ");
  return `${names.charAt(0).toUpperCase()}${names.slice(1)} only`;
}

/**
 * The theme a document actually prints: Classic when its class is locked or
 * does not allow the stored theme (the server falls back the same way and
 * keeps the stored look, so switching the class back restores it).
 */
export function effectiveTheme(
  look: DocumentLook | null | undefined,
  lookLocked: string | null | undefined,
  lookThemes: readonly ThemeId[] | null | undefined,
): ThemeId {
  const id = normaliseLook(look).theme;
  return lookLocked || !themeAllowed(id, lookThemes) ? "classic" : id;
}

// Chapters --------------------------------------------------------------------
//
// Index gives one colour per chapter (Olivia, 5 Oct, answer A):
//   - a chapter is a NUMBERED level-1 heading — the class's top numbered
//     level (\section in article, \chapter in report/book; \part never);
//   - unnumbered headings (\section*) do not advance the sequence and keep
//     the neutral ink, and so does everything under them;
//   - an appendix restarts the sequence: A takes colour 1 again;
//   - a pin is keyed by the heading's block id, so it survives reordering;
//     unpinned chapters fill the sequence in order, skipping pinned colours.

/** A chapter: what Index gives a colour to. */
export interface Chapter {
  id: string;
  title: string;
  /** What carries the index besides colour: "3", or "A" in the appendix. */
  label: string;
  /** 0-based position within its run (main matter, or the appendix). */
  position: number;
  appendix: boolean;
}

interface BlockLike {
  id: string;
  type: string;
  content?: Record<string, unknown> | null;
}

export function headingLevel(block: BlockLike): number {
  const level = Number(block.content?.level);
  return Number.isFinite(level) && level > 0 ? level : 1;
}

function isNumbered(block: BlockLike): boolean {
  return block.content?.numbered !== false;
}

/** The editor's \appendix: a back-matter block of the appendix kind. */
export function isAppendixMarker(block: BlockLike): boolean {
  return block.type === "backMatter" && block.content?.subType === "appendix";
}

function plainText(value: unknown): string {
  return typeof value === "string" ? value.replace(/<[^>]*>/g, "").trim() : "";
}

function appendixLabel(position: number): string {
  let n = position;
  let s = "";
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

/** The document's chapters, in order, with their labels. */
export function documentChapters(blocks: readonly BlockLike[]): Chapter[] {
  const chapters: Chapter[] = [];
  let appendix = false;
  let position = 0;
  for (const b of blocks) {
    if (isAppendixMarker(b)) {
      if (!appendix) { appendix = true; position = 0; }
      continue;
    }
    if (b.type !== "heading" || headingLevel(b) !== 1 || !isNumbered(b)) continue;
    chapters.push({
      id: b.id,
      title: plainText(b.content?.text) || "Untitled",
      label: appendix ? appendixLabel(position) : String(position + 1),
      position,
      appendix,
    });
    position += 1;
  }
  return chapters;
}

/** A pin is an index into the sequence; anything else is ignored. */
export function validPin(pin: unknown, sequence: readonly string[]): number | null {
  return typeof pin === "number" && Number.isInteger(pin) && pin >= 0 && pin < sequence.length ? pin : null;
}

/** One chapter's place in the sequence. */
export interface ChapterColour {
  chapterId: string;
  label: string;
  sequenceIndex: number;
  colour: string;
  pinned: boolean;
}

/**
 * Each chapter's colour. Pinned chapters take their pin; the others take the
 * free colours in order (each run — main matter, appendix — on its own), and
 * only once every colour is taken do they wrap round the whole sequence.
 */
export function chapterColours(
  chapters: readonly Chapter[],
  sequence: readonly string[],
  pins: Readonly<Record<string, number>> = {},
): Map<string, ChapterColour> {
  const out = new Map<string, ChapterColour>();
  if (!sequence.length) return out;
  for (const appendix of [false, true]) {
    const run = chapters.filter((c) => c.appendix === appendix);
    const taken = new Set<number>();
    for (const c of run) {
      const p = validPin(pins[c.id], sequence);
      if (p !== null) taken.add(p);
    }
    const free = sequence.map((_, i) => i).filter((i) => !taken.has(i));
    let k = 0;
    for (const c of run) {
      const p = validPin(pins[c.id], sequence);
      const index = p !== null ? p : free.length ? free[k % free.length] : k % sequence.length;
      if (p === null) k += 1;
      out.set(c.id, { chapterId: c.id, label: c.label, sequenceIndex: index, colour: sequence[index], pinned: p !== null });
    }
  }
  return out;
}

/**
 * The colour of every heading that sits in a chapter: the chapter's own
 * heading and everything under it until the next level-1 heading. Headings
 * before the first chapter, or under an unnumbered one, are absent: they keep
 * the neutral ink.
 */
export function headingColours(
  blocks: readonly BlockLike[],
  sequence: readonly string[],
  pins: Readonly<Record<string, number>> = {},
): Map<string, ChapterColour> {
  const byChapter = chapterColours(documentChapters(blocks), sequence, pins);
  const out = new Map<string, ChapterColour>();
  let current: ChapterColour | undefined;
  for (const b of blocks) {
    if (isAppendixMarker(b)) { current = undefined; continue; }
    if (b.type !== "heading") continue;
    if (headingLevel(b) === 1) current = byChapter.get(b.id);
    if (current) out.set(b.id, current);
  }
  return out;
}

// Thumbnails ------------------------------------------------------------------

/** `#RRGGBB` (or `#RGB`) at an alpha, as rgba(); anything else is returned as is. */
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** What a schematic page is drawn from: never content, never a render. */
export interface ThumbSpec {
  paper: string;
  kicker: string;
  heading: string;
  heading2: string;
  line: string;
  /** Index's spine band: the first six chapter colours. */
  band: string[] | null;
}

/** The schematic page for a theme (2c): paper, kicker, two heading bars, lines. */
export function thumbSpec(theme: ThemeDescriptor): ThumbSpec {
  return {
    paper: theme.colours.paper,
    kicker: theme.colours.kicker,
    heading: theme.colours.heading,
    heading2: theme.colours.accent,
    line: hexToRgba(theme.colours.ink, 0.2),
    band: theme.sequence && theme.sequence.length ? theme.sequence.slice(0, 6) : null,
  };
}

// The canvas --------------------------------------------------------------------
//
// The canvas takes the theme's heading face and colour; body text keeps the
// editor's reading font. The page takes the paper (Olivia, REPLY-2026-10-06d
// §2): in Light the theme's paper and ink (white with "White always"); in Dark
// a coloured paper becomes its dark-paper pair, and everything else keeps
// today's dark page. The surround is editor chrome either way. Classic changes
// nothing.

function luminance(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG contrast ratio of two `#RRGGBB` colours (1 when either is unreadable). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return 1;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The heading colour on a (light) page, white unless another ground is given.
 * A theme drawn for a dark paper (Exposition's gold on burgundy) would vanish
 * on white, so the canvas takes its print-safe ink instead, the same swap
 * Print-safe makes; and when neither holds 4.5:1 on the ground the heading
 * keeps the editor's ink.
 */
export function canvasHeadingColour(theme: ThemeDescriptor, ground = "#FFFFFF"): string | null {
  if (contrastRatio(theme.colours.heading, ground) >= 4.5) return theme.colours.heading;
  if (contrastRatio(theme.printSafe.ink, ground) >= 4.5) return theme.printSafe.ink;
  return null;
}

/** Body ink on a white page: the theme's, else its print-safe ink, else the editor's (null). */
function inkOnWhite(theme: ThemeDescriptor): string | null {
  if (contrastRatio(theme.colours.ink, "#FFFFFF") >= 4.5) return theme.colours.ink;
  if (contrastRatio(theme.printSafe.ink, "#FFFFFF") >= 4.5) return theme.printSafe.ink;
  return null;
}

/**
 * How the canvas page shows the paper: its true colour (Light), or a dark
 * stand-in for it (Dark: the dark-paper pair, or today's dark page).
 */
export type PaperMode = "true" | "dark-tint";

/** The page's ground, ink and heading colour for a look, before the heading font. */
export interface CanvasPaper {
  /** The page ground, or null to keep the editor's page (`--lilia-surface`). */
  paper: string | null;
  /** The body ink, or null to keep the editor's. */
  ink: string | null;
  /** The heading colour (not for Index, whose headings take their chapter's), or null. */
  heading: string | null;
  /** True when the page is a dark-paper pair: its heading colour is final, not lifted again. */
  tinted: boolean;
}

/** The page for a theme and paper setting, on a Light or a Dark editor. */
export function canvasPaper(theme: ThemeDescriptor, paper: LookPaper, dark: boolean): CanvasPaper {
  if (dark) {
    const pair = paper === "theme" ? darkPaperFor(theme.id) : null;
    if (pair) return { paper: pair.paper, ink: pair.ink, heading: pair.heading, tinted: true };
    // White paper, or "White always": today's dark page, headings as phase 1.
    return { paper: null, ink: null, heading: canvasHeadingColour(theme), tinted: false };
  }
  if (paper === "white") {
    return { paper: "#FFFFFF", ink: inkOnWhite(theme), heading: canvasHeadingColour(theme), tinted: false };
  }
  const ground = theme.colours.paper;
  return { paper: ground, ink: theme.colours.ink, heading: canvasHeadingColour(theme, ground), tinted: false };
}

export interface CanvasLook {
  theme: DocumentLook["theme"];
  /** The look's paper setting: Theme's paper or White always. */
  paper: LookPaper;
  /** Whether the page shows the true paper (Light) or a dark stand-in (Dark). */
  paperMode: PaperMode;
  /** The page is a dark-paper pair (Carnet, Gazette, Exposition in Dark). */
  paperTinted: boolean;
  /** CSS custom properties for the canvas root. */
  vars: Record<string, string>;
  /** Index: the colour of each heading that sits in a chapter. */
  headingColours: Map<string, ChapterColour>;
  /** Index: per-heading rules, scoped under `scope`. Empty otherwise. */
  css: string;
  /** The theme's accent. */
  accent: string;
  /** The kicker's tab outside Index (cerulean, terracotta, Gazette's charcoal), as the .sty draws it. */
  kicker: string;
}

const SAFE_ID = /^[A-Za-z0-9_-]+$/;
const SAFE_COLOUR = /^#[0-9A-Fa-f]{3,8}$/;

/**
 * What the canvas needs for a document's look, or null for Classic (and for
 * a theme whose descriptor hasn't loaded): the heading font and colour and the
 * page's paper and ink as CSS variables on the canvas root, and — for Index —
 * one rule per heading in a chapter, setting the heading variable to the
 * chapter's colour. `dark` is the editor's Dark theme.
 */
export function buildCanvasLook(
  look: DocumentLook | null | undefined,
  theme: ThemeDescriptor | undefined,
  blocks: readonly BlockLike[],
  headingFont: string,
  scope: string,
  dark = false,
): CanvasLook | null {
  const l = normaliseLook(look);
  if (l.theme === "classic" || !theme || theme.id !== l.theme) return null;
  const vars: Record<string, string> = { "--lilia-look-heading-font": headingFont };
  const page = canvasPaper(theme, l.paper, dark);
  if (page.paper) vars["--lilia-look-paper"] = page.paper;
  if (page.ink) vars["--lilia-look-ink"] = page.ink;
  // The paper as it prints, whatever the editor's mode: in Dark, a drawing
  // (a TikZ figure, a transparent image) sits on a panel of it (TikZ figures,
  // step 1). White always prints on white; Classic has no look and falls back
  // to white where the variable is read.
  vars["--lilia-look-true-paper"] = l.paper === "white" ? "#FFFFFF" : theme.colours.paper;
  const base = {
    theme: l.theme,
    paper: l.paper,
    paperMode: (dark ? "dark-tint" : "true") as PaperMode,
    paperTinted: page.tinted,
    accent: theme.colours.accent,
    kicker: theme.colours.kicker || theme.colours.accent,
  };
  const sequence = theme.sequence ?? [];
  if (!sequence.length) {
    if (page.heading) vars["--lilia-look-heading"] = page.heading;
    return { ...base, vars, headingColours: new Map(), css: "" };
  }
  // Index: headings outside a chapter keep the neutral ink, so there is no
  // root colour; each chaptered heading gets its own.
  const headingColoursMap = headingColours(blocks, sequence, l.pins);
  const rules: string[] = [];
  for (const [id, cc] of headingColoursMap) {
    if (!SAFE_ID.test(id) || !SAFE_COLOUR.test(cc.colour)) continue;
    rules.push(`${scope} [data-block-id="${id}"]{--lilia-look-heading:${cc.colour}}`);
  }
  return { ...base, vars, headingColours: headingColoursMap, css: rules.join("\n") };
}

/**
 * The canvas page's test attributes (`canvas-page`): the paper setting, and
 * whether the page shows the true paper or a dark stand-in. Classic (no look)
 * reads as Theme's paper, its mode from the editor theme.
 */
export function canvasPageAttrs(look: CanvasLook | null | undefined, dark: boolean): {
  "data-paper": LookPaper;
  "data-paper-mode": PaperMode;
} {
  return {
    "data-paper": look?.paper ?? "theme",
    "data-paper-mode": look?.paperMode ?? (dark ? "dark-tint" : "true"),
  };
}
